import { z } from 'zod';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentPayloadValidator, ComponentValidator } from '../Component';
import { AbstractPort, Port } from '../fields/AbstractPort';
import { ParallelableCapacity } from '../fields/ParallelableCapacity';

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    DC: Port.DC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    capacitance: Port.capacitance.validator,
    reverseDiode: z.boolean().default(false),
    equivalentSeriesResistance: Port.equivalentSeriesResistance.validator,
    isolated: Port.isolated.validator,
    parallelableCapacity: ParallelableCapacity.validator,
    powerFlowDirection: Port.powerFlowDirection.literals.output,
}).default({});

const FuelCellBase = z.object({
    type: z.literal('fuelCell').default('fuelCell'),
    electrical: z
        .object({
            ports: z.tuple([port]).default([undefined]),
        })
        .default({}),
});

export const FuelCellValidator = ComponentValidator.merge(FuelCellBase);
export const FuelCellPayloadValidator = ComponentPayloadValidator.merge(FuelCellBase);

export const FuelCellDefinition: ComponentDefinition = {
    type: 'fuelCell',
    name: 'Fuel Cell',
    plural: 'Fuel Cells',
    indicator: 'FC',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    ports: {
        min: 1,
        max: 1,
        powerFlowDirections: ['output'],
        allowedControlMethods: [
            'constant-power',
            'power-voltage',
            'constant-voltage',
            'constant-voltage-frequency',
            'constant-active-reactive-power',
        ],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: FuelCellValidator,
        payload: FuelCellPayloadValidator,
    },
};

export type FuelCell = z.infer<typeof FuelCellValidator>;
export type FuelCellPayload = z.infer<typeof FuelCellPayloadValidator>;
